import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Trash2, ImagePlus, Clock } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Seo } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { api, formatApiError, pictureUrl } from '../lib/api';
import { SubmitPhotoDialog } from '../components/SubmitPhoto';

const AdminGallery = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/login', { replace: true });
    else if (!user.is_admin) navigate('/medlem', { replace: true });
  }, [user, loading, navigate]);

  const load = () => api.get('/admin/gallery').then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const approve = async (id) => {
    try { await api.post(`/admin/gallery/${id}/approve`); setPhotos(photos.map((p) => (p.id === id ? { ...p, status: 'approved' } : p))); toast.success('Godkjent!'); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm('Slette biletet?')) return;
    try { await api.delete(`/admin/gallery/${id}`); setPhotos(photos.filter((p) => p.id !== id)); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user?.is_admin) return <div className="min-h-screen bg-black" />;
  const pending = photos.filter((p) => p.status === 'pending');
  const approved = photos.filter((p) => p.status === 'approved');

  const Grid = ({ list, testId }) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={testId}>
      {list.map((p) => (
        <div key={p.id} className="relative aspect-[3/2] rounded-2xl overflow-hidden bg-[#1a1424] border border-white/5 group" data-testid={`admin-photo-${p.id}`}>
          <img src={pictureUrl(p.src)} alt={p.alt} className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
            <p className="text-white text-xs font-semibold truncate">{p.alt}</p>
            <p className="text-white/50 text-[10px] truncate">{p.submitted_by}</p>
          </div>
          <div className="absolute top-2 right-2 flex gap-1">
            {p.status === 'pending' && <button type="button" onClick={() => approve(p.id)} aria-label="Godkjenn" data-testid={`admin-photo-approve-${p.id}`} className="w-8 h-8 rounded-full bg-emerald-500 text-black flex items-center justify-center hover:bg-emerald-400"><Check size={15} /></button>}
            <button type="button" onClick={() => remove(p.id)} aria-label="Slett" data-testid={`admin-photo-delete-${p.id}`} className="w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-black min-h-screen text-white" data-testid="admin-gallery-page">
      <Seo title="Galleri – admin | Refuel Weekend" description="Administrer galleriet." path="/admin/galleri" noindex />
      <Navbar />
      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 border border-white/15 text-white hover:bg-black/70 transition-colors text-sm mb-8"><ArrowLeft size={16} /> Adminpanel</Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-sm mb-2">Admin</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>Galleri</h1>
          </div>
          <button type="button" onClick={() => setOpen(true)} data-testid="admin-gallery-upload" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#ff2e2e] text-black font-bold hover:bg-[#ff4d4d]"><ImagePlus size={18} /> Last opp bilete</button>
        </div>
        <h2 className="text-lg font-bold mb-3 inline-flex items-center gap-2"><Clock size={16} className="text-[#ffb400]" /> Ventar på godkjenning ({pending.length})</h2>
        {pending.length ? <Grid list={pending} testId="pending-grid" /> : <p className="text-white/40 text-sm">Ingen bilete ventar.</p>}
        <h2 className="text-lg font-bold mt-10 mb-3">Publiserte ({approved.length})</h2>
        {approved.length ? <Grid list={approved} testId="approved-grid" /> : <p className="text-white/40 text-sm">Ingen opplasta bilete enno. (Dei faste bileta ligg i koden.)</p>}
      </main>
      <Footer />
      {open && <SubmitPhotoDialog onClose={() => setOpen(false)} onDone={load} />}
    </div>
  );
};

export default AdminGallery;
